import React, { useEffect, useState } from 'react';
import { Text } from '@shopify/polaris';
import { apiFetch } from '../lib/api';

/**
 * ONE slim notice for the shop's orders-access state.
 *
 * The backend keeps a single per-shop state (orders-access.service:
 * ok | pcd_not_approved | scope_missing | error) that every orders read
 * updates. While Shopify is still reviewing Autonoma's protected-
 * customer-data access (status 'pcd_not_approved'), sales figures
 * can't be read anywhere — this notice says so ONCE, in the same words,
 * on the Dashboard and the Split Testing page, and disappears on its
 * own as soon as a read succeeds (the state flips to 'ok' immediately).
 *
 * Nothing else is shown here: a missing scope or a transient error is
 * explained in place by the surfaces that hit it (split-test cards).
 */
export const ORDERS_ACCESS_NOTICE_TEXT = "Sales figures will appear once Shopify approves Autonoma's access to order data. Everything else works normally.";

export const ORDERS_ACCESS_PATH = '/api/settings/orders-access';

/** True when the notice should show for this state. */
export function showOrdersAccessNotice(state) {
  return state?.status === 'pcd_not_approved';
}

/** Pure view — the notice for a given state (null when nothing to say). */
export function OrdersAccessNoticeView({ state }) {
  if (!showOrdersAccessNotice(state)) return null;
  return (
    <div
      data-testid="orders-access-notice"
      role="status"
      style={{
        background: '#fff8e6', border: '1px solid #f5e3b3', borderRadius: 10,
        padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 10,
      }}
    >
      <span style={{ fontSize: 15 }} aria-hidden="true">ℹ️</span>
      <Text as="span" variant="bodySm">{ORDERS_ACCESS_NOTICE_TEXT}</Text>
    </div>
  );
}

/**
 * Fetches the state once per mount (and again when `refreshKey`
 * changes — the Split Testing page bumps it after a metrics refresh,
 * which is when a freshly approved store flips to 'ok').
 */
export default function OrdersAccessNotice({ shop, refreshKey = 0 }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!shop) return undefined;
    let cancelled = false;
    apiFetch(ORDERS_ACCESS_PATH, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (!cancelled && data && data.status) setState(data); })
      .catch(() => { /* no notice is better than a wrong one */ });
    return () => { cancelled = true; };
  }, [shop, refreshKey]);

  return <OrdersAccessNoticeView state={state} />;
}
